import React from 'react';
import { FieldValues } from 'react-hook-form';
import FadeTransition from '../../animations/transitions/fade-transition';
import FormProvider from '../../primitives/form-provider';
import FormRegion from '../../primitives/form-region';
import FormFieldset from '../../primitives/form-fieldset';
import Input from '../../primitives/input';
import StyledButton from '../../primitives/styled-button';
import FormSuccess from '../../primitives/form-success';
import Select, { SelectOption } from '../../primitives/select';
import useGeoByAddress from '../../../hooks/use-geo-by-address';
import RadioGroup, { RadioItem } from '../../primitives/radio-group';
import TextArea from '../../primitives/text-area';
import { canadianProvincesAndTerritories, usStatesAndTerritories } from '../../../constants/states';
import Divider from '../../primitives/divider';
import RowSet from '../../primitives/grid/row-set';

const usAndCanStates = usStatesAndTerritories.concat(canadianProvincesAndTerritories);

interface PreferredVendorFormProps {
    submitButton?: string
    successMessage?: string
}

const serviceOptions = [
    'Answering Phone Services',
    'Asset Tracking',
    'Auto Glass Repair',
    'Auto Painting',
    'Awards & Employee Recognition',
    'Background Screening and Drug Testing',
    'Boxes and Packaging Supplies',
    'Car Rentals',
    'Cleaning Supplies',
    'Computers and Technology',
    'Construction Software',
    'Contents Listing Software',
    'Credit Card Processing',
    'Credit Cards',
    'Custom Shelving',
    'Debt Collections',
    'Digital Marketing',
    'Dry Ice Blasting Equipment',
    'Dumpster Rental',
    'Electrical (Suppliers and Subcontractors)',
    'Emergency Board Up',
    'Environmental Contracting/Hygienist/Safety Consulting',
    'Environmental Testing',
    'Equipment',
    'Equipment Rental',
    'Financial/Accounting Consultation',
    'Fleet Purchasing Card',
    'Fleet Safety Solutions',
    'Flooring (Suppliers and Subcontractors)',
    'Flooring Analysis and Testing',
    'Fuel Delivery',
    'General Contracting and Consulting',
    'GPS Tracking',
    'Hotels/Lodging',
    'HVAC (Suppliers and Subcontractors)',
    'Insurance (Life or Health)',
    'Job Search',
    'Legal Services',
    'Lock Boxes',
    'Managed Print Services',
    'Maps',
    'Meal Food Packages',
    'Mobile Offices',
    'Moving Companies',
    'Office Resources',
    'Operating Supplies',
    'Other',
    'Payroll/Human Resources',
    'Personality/Sales Assessments',
    'Portable Containers',
    'Portable Restrooms and Kitchens',
    'Portable Shelters',
    'Printing Services (Flyers, Business Cards, Stationary)',
    'Promotional/Marketing Items',
    'Recruitment Personnel Services',
    'Restoration Services (Document, Art, and Electronics)',
    'Roofing',
    'Safety Products',
    'Sales Leads',
    'Security Services',
    'Signage',
    'Tarps',
    'Temporary Housing',
    'Temporary Labor',
    'Tents (Promotional)',
    'Textile Dry Cleaning',
    'Time and Attendance',
    'Tires',
    'Towels',
    'Trade Show Services',
    'Training',
    'Transcription Services',
    'Truck Rental Services',
    'Uniforms and Branded Apparel',
    'Vehicle Maintenance',
    'Vehicle Wraps',
    'Vendor Management',
    'Voice and Data Services',
    'Waste Management Services (Debris Removal and Hazardous Materials)',
    'Water Treatment Technology',
    'Wireless Services',
];

const PreferredVendorForm = ({ submitButton, successMessage }: PreferredVendorFormProps): JSX.Element => {
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const [currentlyProvidingIsDirty, setCurrentlyProvidingIsDirty] = React.useState(false);
    const [referredByIsDirty, setReferredByIsDirty] = React.useState(false);

    const { data, fetchGeo } = useGeoByAddress();

    const successMessageRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (showSuccess && successMessageRef?.current) {
            setTimeout(() => {
                if (successMessageRef?.current) {
                    successMessageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 500);
        }
    }, [showSuccess]);

    const onSubmit = (e: FieldValues): void => {
        setIsLoading(true);
        fetch('/api/forms/vendor-lead', {
            method: 'POST',
            body: JSON.stringify(e),
            headers: {
                'Content-Type': 'application/json',
                'cache-control': 'no-cache',
            },
        }).then((res) => {
            setIsLoading(false);
            return res.json();
        }).then(() => {
            setShowSuccess(true);
            return true;
        }).catch(() => {
            setIsLoading(false);
            setShowSuccess(false);
            return false;
        });
    };

    return (
        <FadeTransition shouldChange={showSuccess.toString()}>
            {!showSuccess && (
                <FormProvider
                    onSubmit={onSubmit}
                    disabled={isLoading || showSuccess}
                    values={{
                        city: data?.city,
                        state: data?.stateShort,
                    }}
                >
                    <RowSet breakpoints={{
                        dt: { between: 32 },
                        mb: { between: 16 },
                    }}
                    >
                        <FormRegion description="Company Information">
                            <FormFieldset alignLeft name="">
                                <span>
                                    <Input name="company" maxLength={40} placeholder="Company Name" required />
                                </span>
                                <span>
                                    <Input
                                        onChange={(e) => {
                                            if (e?.currentTarget?.value && e.currentTarget.value.length === 5) {
                                                fetchGeo(e.currentTarget.value);
                                            }
                                        }}
                                        minLength={5}
                                        maxLength={20}
                                        required
                                        name="zip"
                                        placeholder="Zip"
                                    />
                                </span>
                            </FormFieldset>
                            {data && data?.city && (
                                <FormFieldset alignLeft name="">

                                    <>
                                        <div css={{ width: '100%' }}>
                                            <Input name="street" placeholder="Company HQ Street Address" required />
                                        </div>
                                        <span>
                                            <Input required name="city" placeholder="City" />
                                        </span>
                                        <span>
                                            <Select required name="state" placeholder="State/Province">
                                                {usAndCanStates.map((item, index) => (
                                                    <SelectOption key={`state-${index}`} value={item.abbreviation}>
                                                        {item.name}
                                                    </SelectOption>
                                                ))}
                                            </Select>
                                        </span>
                                    </>

                                </FormFieldset>
                            )}
                            <FormFieldset alignLeft name="">
                                <Input name="url" maxLength={80} placeholder="Website" required />
                            </FormFieldset>
                        </FormRegion>
                        <Divider />
                        <FormRegion description="Contact Information">
                            <FormFieldset alignLeft name="Does your company have physical locations outside of the U.S.?">
                                <RadioGroup name="physical-locations-outside-us" required>
                                    <RadioItem value="true">
                                        Yes
                                    </RadioItem>
                                    <RadioItem value="false">
                                        No
                                    </RadioItem>
                                </RadioGroup>
                            </FormFieldset>
                            <FormFieldset alignLeft name="">
                                <span>
                                    <Input name="first-name" placeholder="Your First Name" required />
                                </span>
                                <span>
                                    <Input name="last-name" placeholder="Your Last Name" required />
                                </span>
                                <span>
                                    <Input name="title" placeholder="Title" />
                                </span>
                                <span>
                                    <Input name="phone" type="tel" placeholder="Phone Number" autoComplete="tel" />
                                </span>
                                <span>
                                    <Input name="email" placeholder="Email Address" autoComplete="email" required />
                                </span>
                            </FormFieldset>
                        </FormRegion>
                        <Divider />
                        <FormRegion description="Product/Service Details">
                            <FormFieldset alignLeft name="Product/Services Provided">
                                <div css={{ width: '100%' }}>
                                    <Select required name="service-provided" placeholder="Choose...">
                                        {serviceOptions.map((item, index) => (
                                            <SelectOption key={`state-${index}`} value={item}>
                                                {item}
                                            </SelectOption>
                                        ))}
                                    </Select>
                                </div>
                            </FormFieldset>
                            <FormFieldset alignLeft name="Products/Services Additional Notes:">
                                <TextArea
                                    name="service-notes"
                                    placeholder="Additional Notes"
                                />
                            </FormFieldset>
                            <FormFieldset alignLeft name="Are you able to provide your products and services Nationwide in the United States?">
                                <RadioGroup name="can-provide-nationwide-in-us" required>
                                    <RadioItem value="true">
                                        Yes
                                    </RadioItem>
                                    <RadioItem value="false">
                                        No
                                    </RadioItem>
                                </RadioGroup>
                            </FormFieldset>
                            <FormFieldset alignLeft name="Can you provide products and services to Canada?">
                                <RadioGroup name="can-provide-to-ca" required>
                                    <RadioItem value="true">
                                        Yes
                                    </RadioItem>
                                    <RadioItem value="false">
                                        No
                                    </RadioItem>
                                </RadioGroup>
                            </FormFieldset>
                            <FormFieldset alignLeft name="Are you currently providing products/services to SERVPRO® Franchises?">
                                <RadioGroup
                                    onChange={(e) => {
                                        setCurrentlyProvidingIsDirty(e === 'true');
                                    }}
                                    name="currently-provides-services-to-servpro"
                                    required
                                >
                                    <RadioItem value="true">
                                        Yes
                                    </RadioItem>
                                    <RadioItem value="false">
                                        No
                                    </RadioItem>
                                </RadioGroup>
                            </FormFieldset>
                            {currentlyProvidingIsDirty && (
                                <FormFieldset alignLeft name="How much revenue are you currently doing with SERVPRO Franchises annually?">
                                    <Input name="revenue-to-servpro-franchisees-annually" placeholder="Revenue" type="text" required />
                                </FormFieldset>
                            )}
                        </FormRegion>
                        <Divider />
                        <FormRegion description="Referral/Franchise Information">
                            <FormFieldset alignLeft name="Were you referred by a Corporate or Franchise Employee?">
                                <RadioGroup
                                    onChange={(e) => {
                                        setReferredByIsDirty(e === 'true');
                                    }}
                                    name="referred-by-corporate-or-franchise-employee"
                                    required
                                >
                                    <RadioItem value="true">
                                        Yes
                                    </RadioItem>
                                    <RadioItem value="false">
                                        No
                                    </RadioItem>
                                </RadioGroup>
                            </FormFieldset>
                            {referredByIsDirty && (
                                <FormFieldset alignLeft name="Extra Information">
                                    <TextArea
                                        name="referrer"
                                        hideLabel
                                        placeholder="If applicable, please provide the Franchise Name(s), Contact Name(s), Contact Number(s), and how long you have been providing your products or services to SERVPRO® Franchises."
                                    />
                                </FormFieldset>
                            )}
                        </FormRegion>

                        <FormRegion description="">
                            <FormFieldset alignLeft name="">
                                <StyledButton type="submit" loading={isLoading}>
                                    {submitButton || 'Submit'}
                                </StyledButton>
                            </FormFieldset>
                        </FormRegion>
                    </RowSet>
                </FormProvider>
            )}
            <div ref={successMessageRef}>
                {showSuccess && (
                    <FormSuccess dangerouslySetInnerHTML={{ __html: successMessage || 'Submitted successfully.' }} />
                )}
            </div>
        </FadeTransition>
    );
};

export default PreferredVendorForm;
